import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import InfoOutlineIcon from '@mui/icons-material/InfoOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { Alert, Box, Button, FormControl, IconButton, Stack, Tooltip } from '@mui/material'
import { DataGridPro, GridColDef, GridRowId } from '@mui/x-data-grid-pro'
import { connectField } from 'dynamic-uniforms'
import { useCallback, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Markdown } from '../../../Markdown'
import { useCiboAutoForm } from '../CiboAutoFormContext'
import { FieldLabel } from '../FieldLabel'
import { filterDOMProps } from '../filterDOMProps'
import { getNumberFormatter } from '../getNumberFormatter'
import { ValidationMessages } from '../ValidationMessages'

type PrimitivePropertyType = 'string' | 'number' | 'integer'

type SimplePropertySchema = {
  type?: string
  title?: string
  'ui-params'?: {
    numberFormat?:
      | boolean
      | { enabled?: boolean; locale?: string; options?: Intl.NumberFormatOptions }
  }
}

type ObjectItemsSchema = {
  type?: string
  properties?: Record<string, SimplePropertySchema>
}

type ArraySchema = {
  type?: string
  items?: ObjectItemsSchema
}

type DataGridFieldProps = {
  value?: Array<Record<string, string | number | undefined>>
  onChange: (value: Array<Record<string, string | number | undefined>>) => void
  name: string
  label?: string
  required?: boolean
  disabled?: boolean
  error?: { message: string }
  field?: ArraySchema
  description?: string
  [key: string]: any
}

type ValidationResult = {
  error?: string
  properties?: Record<string, SimplePropertySchema>
}

type MessageLookup = Record<string, string>

type AjvLikeErrorDetail = {
  message?: string
  name?: string
  path?: string
  instancePath?: string
  keyword?: string
  params?: { missingProperty?: string }
}

const SUPPORTED_PROPERTY_TYPES: PrimitivePropertyType[] = ['string', 'number', 'integer']

const hasUnsupportedIncomingItemValue = (
  item: unknown
): item is Record<string, unknown> | Array<unknown> | string | number | boolean => {
  if (!item || typeof item !== 'object' || Array.isArray(item)) {
    return true
  }

  return Object.values(item).some(
    cellValue => cellValue != null && typeof cellValue !== 'string' && typeof cellValue !== 'number'
  )
}

const normalizePath = (path: string) => path.replace(/^\//, '').replace(/\//g, '.')

const registerMessage = (messages: MessageLookup, path: string, message: string) => {
  const normalizedPath = normalizePath(path)
  const slashPath = normalizedPath.replace(/\./g, '/')

  messages[path] = message
  messages[normalizedPath] = message
  messages[slashPath] = message
  messages[`/${slashPath}`] = message
}

const pathsFromAjvDetail = (detail: AjvLikeErrorDetail) => {
  const paths = new Set<string>()
  const basePath = detail.name || detail.path || detail.instancePath

  if (basePath) {
    const normalizedBase = normalizePath(basePath)

    paths.add(basePath)
    paths.add(normalizedBase)
    paths.add(normalizedBase.replace(/\./g, '/'))
  }

  if (detail.keyword === 'required' && detail.params?.missingProperty) {
    const parentPath = normalizePath(basePath || '')
    const requiredPath = parentPath
      ? `${parentPath}.${detail.params.missingProperty}`
      : detail.params.missingProperty

    paths.add(requiredPath)
    paths.add(requiredPath.replace(/\./g, '/'))
  }

  return Array.from(paths)
}

const collectErrorMessages = (formError: unknown): MessageLookup => {
  const messages: MessageLookup = {}

  if (!formError || typeof formError !== 'object') {
    return messages
  }

  const maybeError = formError as {
    details?: Array<AjvLikeErrorDetail>
    [key: string]: unknown
  }

  if (Array.isArray(maybeError.details)) {
    for (const detail of maybeError.details) {
      if (!detail.message) {
        continue
      }

      const messagePaths = pathsFromAjvDetail(detail)
      for (const path of messagePaths) {
        registerMessage(messages, path, detail.message)
      }
    }
  }

  for (const [key, value] of Object.entries(maybeError)) {
    if (typeof value === 'string') {
      registerMessage(messages, key, value)
    }
  }

  return messages
}

const lookupMessage = (lookup: MessageLookup | undefined, candidatePaths: string[]) => {
  if (!lookup) {
    return undefined
  }

  for (const candidate of candidatePaths) {
    const normalizedCandidate = normalizePath(candidate)
    const slashCandidate = normalizedCandidate.replace(/\./g, '/')
    const candidateVariants = [candidate, normalizedCandidate, slashCandidate, `/${slashCandidate}`]

    for (const variant of candidateVariants) {
      if (lookup[variant]) {
        return lookup[variant]
      }
    }
  }

  return undefined
}

const validateTableArraySchema = (schema: ArraySchema | undefined): ValidationResult => {
  if (!schema || schema.type !== 'array') {
    return {
      error: 'Table editor requires a JSON schema with type "array".',
    }
  }

  const itemSchema = schema.items
  if (!itemSchema || itemSchema.type !== 'object') {
    return {
      error: 'Table editor requires items to be a schema object with type "object".',
    }
  }

  const properties = itemSchema.properties
  if (!properties || Object.keys(properties).length === 0) {
    return {
      error: 'Table editor requires item object properties to define editable columns.',
    }
  }

  return { properties }
}

const createEmptyRow = (properties: Record<string, SimplePropertySchema>) => {
  return Object.keys(properties).reduce<Record<string, string | number | undefined>>((row, key) => {
    row[key] = undefined
    return row
  }, {})
}

function DataGridFieldImpl({
  value,
  onChange,
  name,
  label,
  required,
  disabled,
  error,
  description,
  field,
  ...props
}: DataGridFieldProps) {
  const { t } = useTranslation('@cibo/programs/ProgramEngagementPlayer/DataGridField')

  const {
    touch,
    warnings,
    touched,
    error: uniformsFormError,
  } = useCiboAutoForm() as {
    touch: (fieldName: string) => void
    warnings?: MessageLookup
    touched?: Record<string, boolean>
    error?: unknown
  }

  const validation = useMemo(() => validateTableArraySchema(field), [field])
  const hasLoggedUnsupportedIncomingItemsRef = useRef(false)

  const rows = useMemo(() => {
    const arrayValue = Array.isArray(value) ? value : []

    if (!hasLoggedUnsupportedIncomingItemsRef.current) {
      const hasUnsupportedItem = arrayValue.some(item => hasUnsupportedIncomingItemValue(item))

      if (hasUnsupportedItem) {
        console.error(
          `DataGridField received unsupported incoming item value types for field "${name}". ` +
            'Only string and number cell values are supported.'
        )
        hasLoggedUnsupportedIncomingItemsRef.current = true
      }
    }

    return arrayValue.map((item, index) => ({
      id: index,
      ...(item && typeof item === 'object' && !Array.isArray(item) ? item : {}),
    }))
  }, [name, value])

  const writeRows = useCallback(
    (nextRows: Array<Record<string, string | number | undefined> & { id: GridRowId }>) => {
      const nextValue = nextRows.map(
        ({ id, ...rest }) => rest as Record<string, string | number | undefined>
      )
      onChange(nextValue)
      touch(name)
    },
    [name, onChange, touch]
  )

  const handleDeleteRow = useCallback(
    (rowId: GridRowId) => {
      writeRows(rows.filter(row => row.id !== rowId))
    },
    [rows, writeRows]
  )

  const handleAddRow = useCallback(() => {
    if (!validation.properties) {
      return
    }

    const nextId = rows.length === 0 ? 0 : Math.max(...rows.map(row => Number(row.id))) + 1
    const newRow = {
      id: nextId,
      ...createEmptyRow(validation.properties),
    }

    writeRows([...rows, newRow])
  }, [rows, validation.properties, writeRows])

  const errorMessages = useMemo(
    () => ({
      ...collectErrorMessages(uniformsFormError),
      ...collectErrorMessages(error),
    }),
    [error, uniformsFormError]
  )

  const columns = useMemo<GridColDef[]>(() => {
    if (!validation.properties) {
      return []
    }

    const schemaColumns = Object.entries(validation.properties).map(([propertyName, property]) => {
      const propertyType = SUPPORTED_PROPERTY_TYPES.includes(property.type as PrimitivePropertyType)
        ? (property.type as PrimitivePropertyType)
        : 'string'
      const isNumericProperty = propertyType === 'number' || propertyType === 'integer'
      const numberFormatter = isNumericProperty ? getNumberFormatter(property) : undefined

      return {
        field: propertyName,
        headerName: property.title || propertyName,
        editable: !disabled,
        flex: 1,
        minWidth: 140,
        // Keep number columns as plain text unless explicitly opted into formatting.
        type: isNumericProperty && numberFormatter ? 'number' : 'string',
        renderCell: params => {
          const rowIndex = String(params.id)
          const candidatePaths = [`${name}.${rowIndex}.${propertyName}`]

          const warningMessage = lookupMessage(warnings, candidatePaths)
          const errorMessage = lookupMessage(errorMessages, candidatePaths)
          const isTouched =
            !!touched?.[name] ||
            candidatePaths.some(path => !!touched?.[path] || !!touched?.[normalizePath(path)])

          const activeMessage = isTouched ? errorMessage || warningMessage : undefined
          const isError = !!activeMessage && activeMessage === errorMessage

          const displayValue =
            numberFormatter && params.value != null && params.value !== ''
              ? numberFormatter(params.value)
              : params.value == null
              ? ''
              : String(params.value)

          return (
            <Stack
              alignItems="center"
              direction="row"
              justifyContent="space-between"
              sx={{
                height: '100%',
                px: 1,
                width: '100%',
              }}
            >
              <Box
                sx={{
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                <p title={displayValue}>{displayValue}</p>
              </Box>

              {!!activeMessage && (
                <Tooltip title={activeMessage}>
                  {isError ? (
                    <InfoOutlineIcon
                      sx={{
                        color: 'error.main',
                        fontSize: 16,
                        ml: 0.5,
                      }}
                    />
                  ) : (
                    <WarningAmberIcon
                      sx={{
                        color: 'warning.main',
                        fontSize: 16,
                        ml: 0.5,
                      }}
                    />
                  )}
                </Tooltip>
              )}
            </Stack>
          )
        },
        valueParser: (incomingValue: unknown) => {
          if (propertyType === 'string') {
            return incomingValue === '' || incomingValue == null ? undefined : String(incomingValue)
          }

          if (incomingValue === '' || incomingValue == null) {
            return undefined
          }

          const parsedValue = Number(incomingValue)
          if (Number.isNaN(parsedValue)) {
            return undefined
          }

          return propertyType === 'integer' ? Math.trunc(parsedValue) : parsedValue
        },
      } as GridColDef
    })

    return [
      ...schemaColumns,
      {
        field: '__actions__',
        headerName: '',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        width: 56,
        align: 'center',
        renderCell: params => (
          <IconButton
            aria-label={t('deleteRow')}
            disabled={disabled}
            size="small"
            onClick={() => handleDeleteRow(params.id)}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        ),
      },
    ]
  }, [disabled, errorMessages, handleDeleteRow, name, touched, validation.properties, warnings])

  return (
    <FormControl fullWidth {...filterDOMProps(props)} required={!!required} disabled={!!disabled}>
      {label && <FieldLabel label={label} required={required} />}

      {!validation.error ? (
        <Stack spacing={1}>
          <Box sx={{ width: '100%', minHeight: 320 }}>
            <DataGridPro
              rows={rows}
              columns={columns}
              density="compact"
              disableRowSelectionOnClick
              hideFooter
              editMode="cell"
              processRowUpdate={updatedRow => {
                const nextRows = rows.map(row => (row.id === updatedRow.id ? updatedRow : row))
                writeRows(nextRows)
                return updatedRow
              }}
              sx={{
                '& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': {
                  outline: 'none',
                },
              }}
            />
          </Box>

          <Box>
            <Button
              data-testid={`data-grid-add-row-${name}`}
              disabled={disabled}
              size="small"
              onClick={handleAddRow}
              startIcon={<AddIcon />}
              variant="outlined"
            >
              {t('addRow')}
            </Button>
          </Box>
        </Stack>
      ) : (
        <Alert data-testid={`data-grid-field-schema-error-${name}`} severity="error">
          {validation.error}
        </Alert>
      )}

      <ValidationMessages fieldName={name} error={error} />

      {!!description && <Markdown typographyProps={{ variant: 'caption' }}>{description}</Markdown>}
    </FormControl>
  )
}

export const DataGridField = connectField<DataGridFieldProps>(DataGridFieldImpl)
